// VideoPage.jsx
import React, { useEffect } from 'react';
import { Row, Col, Button, Table } from 'reactstrap';
import { Link } from 'react-router-dom';
import xsimage from '../../assets/images/branding-xs.png'

const VideoPage = () => {
    useEffect(() => {
        window.scrollTo(0, 0);
        // Scroll animation observer
        const observer = new IntersectionObserver((entries) => {
            entries.forEach(entry => {
                if (entry.isIntersecting) {
                    entry.target.style.animation = 'fadeInUp 0.8s ease-out forwards';
                }
            });
        }, { threshold: 0.1 });

        document.querySelectorAll('[data-animated]').forEach(el => observer.observe(el));
        return () => observer.disconnect();
    }, []);

    const videoStats = [
        { number: '87%', label: 'of marketers use video as a marketing tool — the highest adoption rate ever recorded' },
        { number: '3x', label: 'more engagement for video content compared to static posts on social platforms' },
        { number: '72%', label: 'of customers prefer learning about products through video rather than text' },
        { number: '50%', label: 'faster decision-making when video is included in the buying journey' }
    ];

    const whyVideoMatters = [
        '🔹 Enhances brand awareness and recognition',
        '🔹 Increases audience engagement across digital channels',
        '🔹 Improves conversion rates and customer trust',
        '🔹 Communicates messages more clearly and memorably than text alone',
        '🔹 Can be repurposed across platforms (social, web, email campaigns)'
    ];

    const videoServices = [
        {
            icon: 'fas fa-lightbulb',
            title: 'Concept & Creative Strategy',
            desc: 'The best video content starts with an idea that supports a clear objective. We help you define the story, message and goals before filming begins.',
            features: ['Creative concept development', 'Campaign-aligned messaging', 'Strategic planning for distribution']
        },
        {
            icon: 'fas fa-clipboard-list',
            title: 'Pre-Production Planning',
            desc: 'Preparation sets the stage for successful execution. We handle all logistical planning so your video shoot runs smoothly.',
            features: ['Scriptwriting & storyboarding', 'Casting and talent coordination', 'Location and production planning']
        },
        {
            icon: 'fas fa-video',
            title: 'Filming & Production',
            desc: 'Our production team brings your visuals to life using professional cinematography, lighting and sound capture.',
            features: ['On-site shooting with experienced crew', 'Studio or location setup', 'High-quality filming equipment']
        },
        {
            icon: 'fas fa-film',
            title: 'Post-Production & Editing',
            desc: 'We transform raw footage into polished, engaging video content ready for your campaign.',
            features: ['Professional editing & colour grading', 'Motion graphics and visual effects', 'Music, VO, and sound design']
        },
        {
            icon: 'fas fa-mobile-alt',
            title: 'Format & Platform Optimisation',
            desc: 'Video should adapt to every channel where your audience is active. We optimise each output for performance.',
            features: ['Social platforms (Reels, TikTok, YouTube Shorts)', 'Website and landing page placement', 'Paid media and advertising formats', 'Long-form and corporate presentations']
        }
    ];

    const videoFormats = [
        { format: '🎯 Brand & Awareness Videos', description: 'Introduce your story and identity' },
        { format: '🛍️ Product & Demo Videos', description: 'Showcase features and benefits' },
        { format: '📣 Social & Advertising Content', description: 'Short, attention-grabbing clips' },
        { format: '👥 Customer Testimonials & Case Studies', description: 'Build credibility' },
        { format: '🎓 Explainers & Tutorials', description: 'Educate your audience clearly' },
        { format: '🎬 Corporate & Event Videos', description: 'Professional coverage for B2B and internal use' }
    ];

    const benefits = [
        ['Higher Engagement', 'Videos naturally attract viewer attention across platforms'],
        ['Improved Conversions', 'Helping audiences understand and act'],
        ['Stronger Brand Recall', 'Visual storytelling strengthens memory'],
        ['Versatile Usage', 'Repurpose video for ads, web, social, and presentations'],
        ['Measurable Performance', 'Track views, engagement, watch time, and conversions']
    ];

    const targetBusinesses = [
        'Brands building higher visibility and awareness through compelling visual storytelling',
        'E-commerce stores promoting products, demonstrating features, and improving conversion rates',
        'SaaS companies explaining features, benefits, and improving customer onboarding',
        'Agencies running paid video campaigns, social content strategies, and integrated video marketing',
        'Organisations leveraging video across SEO, social media, paid advertising, and website engagement'
    ];

    const videoQuestions = [
        'How can video content enhance your brand storytelling and audience engagement?',
        'What types of video content deliver the highest ROI for your specific business objectives?',
        'How do you optimise video content for different platforms and audience preferences?',
        'What production approaches balance creative excellence with commercial effectiveness?',
        'How can video analytics inform content strategy and drive continuous improvement?'
    ];

    const videoTechniques = [
        'Strategic Video Concept Development & Storytelling',
        'Professional Cinematography & Production Techniques',
        'Advanced Video Editing & Post-Production Workflows',
        'Cross-Platform Video Optimisation & Distribution',
        'Video Performance Analytics & Engagement Tracking',
        'Integrated Video Marketing Strategy Development'
    ];

    const whyChooseUs = [
        'Creative direction grounded in marketing strategy',
        'Technical production expertise with professional crews',
        'Content designed for multi-platform performance',
        'Collaborative workflow with transparent communication',
        'Measurable impact tied to brand and business outcomes'
    ];

    return (
        <div className="branding sv-page">
            {/* Decorative Image - Top of Page */}
            <div className="sv-decorative-image-container">

            </div>
            <div className='xs-sv-page'>
                <img src={xsimage} />
            </div>
            {/* Page Title Section */}
            <div className="sv-page-title-section">
                <div className="container">
                    <h1>
                        <span className="sv-gradient-text">Video Production & Creative Video Services</span>
                    </h1>
                    <p>Engage Audiences with Powerful, Strategic Video Content</p>
                </div>
            </div>

            {/* Main Content */}
            <div className="container-fluid sv-content-wrapper">
                <div className="sv-main-content">
                    {/* Hero Content Section */}
                    <section className="sv-hero-section" data-animated>
                        <Row className="align-items-center">
                            <Col lg={8}>
                                <p className="sv-lead">
                                    Video has become one of the most effective ways to connect with audiences online — from brand storytelling to product showcases, ads, social campaigns and beyond.
                                </p>
                                <p className="sv-description">
                                    At Reachtix, our Video Production services combine creative vision, cinematic quality, and marketing strategy to produce video content that resonates, drives engagement, and accelerates your business goals. High-quality video helps you capture attention, boost engagement, and communicate complex ideas simply and memorably.
                                </p>
                                <div className="sv-cta-buttons">
                                    <Button
                                        tag={Link}
                                        to="/contact"
                                        color='dark'
                                    >
                                        <i className="fas fa-rocket mr-2"></i>
                                        Start Video Project
                                    </Button>
                                    <Button
                                        tag={Link}
                                        to="/contact"
                                        color='dark'
                                    >
                                        <i className="fas fa-eye mr-2"></i>
                                        Explore Video Services
                                    </Button>
                                </div>
                            </Col>
                        </Row>
                    </section>

                    {/* Stats Section */}
                    <section className="sv-stats-section">
                        <h2 className="sv-section-title" data-animated>
                            The <span className="sv-text-gradient">Video Engagement Impact</span>
                        </h2>
                        <Row className="sv-stats-grid">
                            {videoStats.map((stat, index) => (
                                <Col lg={6} md={6} key={index} className="mb-4" data-animated>
                                    <div className="sv-stat-card">
                                        <div className="sv-stat-number">{stat.number}</div>
                                        <div className="sv-stat-label">{stat.label}</div>
                                    </div>
                                </Col>
                            ))}
                        </Row>
                    </section>

                    {/* Why Video Matters Section */}
                    <section className="sv-explanation-section" data-animated>
                        <Row>
                            <Col lg={8}>
                                <h2 className="sv-section-title">Why Video Matters in Digital Marketing</h2>
                                <p className="sv-section-description">
                                    Video is no longer an optional content format — it's a core element of modern digital strategy. Whether embedded on websites, shared on social platforms, or used in paid advertising, professional video content:
                                </p>
                                <div className="sv-questions-grid">
                                    {whyVideoMatters.map((item, index) => (
                                        <div key={index} className="sv-question-item">
                                            <span className="sv-question-icon">{item.substring(0, 2)}</span>
                                            {item.substring(3)}
                                        </div>
                                    ))}
                                </div>
                                <p className="sv-section-description mt-3">
                                    From short social clips to long-form storytelling, the right video content moves your audience through the marketing funnel more effectively.
                                </p>
                            </Col>
                        </Row>
                    </section>

                    {/* What Is Video Production Section */}
                    <section className="sv-explanation-section" data-animated>
                        <Row>
                            <Col lg={8}>
                                <h2 className="sv-section-title">What We Deliver</h2>
                                <p className="sv-section-description">
                                    We offer end-to-end video production, supporting brands at every stage — from strategy and concept to final delivery and distribution.
                                </p>
                                <div className="sv-questions-grid">
                                    <h4>Key Video Production Questions We Address:</h4>
                                    {videoQuestions.map((question, index) => (
                                        <div key={index} className="sv-question-item">
                                            <i className="fas fa-question-circle"></i>
                                            {question}
                                        </div>
                                    ))}
                                </div>
                            </Col>
                        </Row>
                    </section>

                    {/* Techniques Section */}
                    <section className="sv-explanation-section" data-animated>
                        <Row>
                            <Col lg={8}>
                                <h2 className="sv-section-title">Advanced Video Production Techniques</h2>
                                <p className="sv-section-description">
                                    We employ sophisticated video production methodologies and creative techniques to create compelling visual content that combines artistic excellence with commercial effectiveness across all digital platforms.
                                </p>
                                <div className="sv-questions-grid">
                                    <h4>Core Video Production Methodologies:</h4>
                                    {videoTechniques.map((technique, index) => (
                                        <div key={index} className="sv-question-item">
                                            <i className="fas fa-cogs"></i>
                                            {technique}
                                        </div>
                                    ))}
                                </div>
                            </Col>
                        </Row>
                    </section>

                    {/* Our Video Services Section */}
                    <section className="sv-approach-section" data-animated>
                        <Row>
                            <Col>
                                <h2 className="sv-section-title">Our Video Services at Reachtix</h2>
                                <p className="sv-section-description">
                                    We offer end-to-end video production, supporting brands at every stage — from strategy and concept to final delivery and distribution.
                                </p>

                                <div className="sv-approaches-grid">
                                    {videoServices.map((item, index) => (
                                        <div key={index} className="sv-approach-card">
                                            <div className="sv-approach-icon">
                                                <i className={item.icon}></i>
                                            </div>
                                            <div className="sv-approach-content">
                                                <h4>{item.title}</h4>
                                                <p>{item.desc}</p>
                                                <ul className="sv-feature-list">
                                                    {item.features.map((feature, idx) => (
                                                        <li key={idx}><i className="fas fa-check-circle"></i> {feature}</li>
                                                    ))}
                                                </ul>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </Col>
                        </Row>
                    </section>

                    {/* Types of Videos Section */}
                    <section className="sv-benefits-section" data-animated>
                        <Row>
                            <Col>
                                <h2 className="sv-section-title">Types of Videos We Produce</h2>
                                <p className="sv-section-description mb-4">
                                    We tailor production to your audience and objectives, covering a wide range of video formats:
                                </p>
                                <div className="table-responsive">
                                    <Table borderless className="sv-benefits-table">
                                        <thead>
                                            <tr>
                                                <th>Video Format</th>
                                                <th>Purpose</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {videoFormats.map((format, index) => (
                                                <tr key={index}>
                                                    <td>
                                                        <i className="fas fa-check-circle"></i>
                                                        <strong>{format.format}</strong>
                                                    </td>
                                                    <td>{format.description}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </Table>
                                </div>
                            </Col>
                        </Row>
                    </section>

                    {/* Benefits Section */}
                    <section className="sv-benefits-section" data-animated>
                        <Row>
                            <Col>
                                <h2 className="sv-section-title">Benefits of Professional Video Content</h2>
                                <div className="table-responsive">
                                    <Table borderless className="sv-benefits-table">
                                        <thead>
                                            <tr>
                                                <th>Benefit</th>
                                                <th>Impact on Your Business</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {benefits.map(([advantage, impact], index) => (
                                                <tr key={index}>
                                                    <td>
                                                        <i className="fas fa-check-circle"></i>
                                                        <strong>{advantage}</strong>
                                                    </td>
                                                    <td>{impact}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </Table>
                                </div>
                                <p className="mt-4 sv-section-description">
                                    Strategic video production transforms your content strategy from informational to experiential, enabling superior audience engagement, improved conversion performance, stronger brand recognition, and measurable return on video investment.
                                </p>
                            </Col>
                        </Row>
                    </section>

                    {/* Who Should Invest Section */}
                    <section className="sv-target-section" data-animated>
                        <Row>
                            <Col>
                                <h2 className="sv-section-title">Who Should Invest in Video Services?</h2>
                                <p className="sv-section-description">
                                    Video production supports businesses at every stage of the customer journey and is ideal for:
                                </p>
                                <div className="sv-target-list">
                                    {targetBusinesses.map((business, index) => (
                                        <div key={index} className="sv-target-item">
                                            <i className="fas fa-chevron-right"></i>
                                            {business}
                                        </div>
                                    ))}
                                </div>
                                <p className="sv-section-description mt-4">
                                    Video production supports businesses at every stage of the customer journey and marketing funnel, from initial awareness building to final conversion optimisation.
                                </p>
                            </Col>
                        </Row>
                    </section>

                    {/* Why Choose Reachtix Section */}
                    <section className="sv-explanation-section" data-animated>
                        <Row>
                            <Col lg={8}>
                                <h2 className="sv-section-title">Why Choose Reachtix for Video Production</h2>
                                <p className="sv-section-description">
                                    Video production is both an art and a science — and success requires strategic alignment with your marketing goals as well as high-quality execution. With Reachtix, you get:
                                </p>
                                <div className="sv-questions-grid">
                                    {whyChooseUs.map((item, index) => (
                                        <div key={index} className="sv-question-item">
                                            <i className="fas fa-check-circle"></i>
                                            {item}
                                        </div>
                                    ))}
                                </div>
                                <p className="sv-section-description mt-3">
                                    We don't just create videos — we craft visual content that supports your broader growth strategy and drives measurable results.
                                </p>
                                <div className="sv-questions-grid mt-4">
                                    <h4>Our Video Production Advantages:</h4>
                                    <div className="sv-question-item">
                                        <i className="fas fa-chess"></i>
                                        Strategic Creative Direction Grounded in Marketing Objectives
                                    </div>
                                    <div className="sv-question-item">
                                        <i className="fas fa-video"></i>
                                        Technical Production Excellence with Professional Crews & Equipment
                                    </div>
                                    <div className="sv-question-item">
                                        <i className="fas fa-layer-group"></i>
                                        Multi-Platform Content Optimisation & Distribution Strategy
                                    </div>
                                    <div className="sv-question-item">
                                        <i className="fas fa-chart-pie"></i>
                                        Performance Measurement & Data-Driven Optimisation Approach
                                    </div>
                                </div>
                            </Col>
                        </Row>
                    </section>

                    {/* Final CTA Section */}
                    <section className="sv-main-cta" data-animated>
                        <Row>
                            <Col>
                                <h2>Ready to Elevate Your Visual Storytelling?</h2>
                                <p className="sv-cta-description">
                                    Video content isn't just creative — it's a strategic asset that drives engagement, builds trust, and propels growth.
                                </p>
                                <p className="sv-cta-description">
                                    At Reachtix, we treat video production as strategic business communication — not just creative expression. Our specialists blend cinematic excellence with marketing insight to make your video content a powerful driver of audience engagement, brand connection, and sustainable business success.
                                </p>
                                <div className="sv-cta-buttons">
                                    <Button
                                        tag={Link}
                                        to="/contact"
                                        color='primary'
                                    >
                                        <i className="fas fa-video mr-2"></i>
                                        Start Video Production
                                    </Button>
                                    <Button
                                        tag={Link}
                                        to="/contact"
                                        color='light'
                                    >
                                        <i className="fas fa-eye mr-2"></i>
                                        Contact Reachtix Today
                                    </Button>
                                </div>
                            </Col>
                        </Row>
                    </section>
                </div>
            </div>
        </div>
    );
};

export default VideoPage;